import { useEffect } from 'react'
import * as Sentry from '@sentry/react'
import { useQuery } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import Cookies from 'js-cookie'
import { OctagonAlert } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { z } from 'zod'

import { api, ApiError } from '@/lib/axios'
import { Toast } from '../components/Toast'
import { COOKIES_KEYS } from '../constants/cookiesKeys'
import { applicationAccountUserModel } from '../models/applicationAccountUser'
import { personModel } from '../models/person'
import { userModel } from '../models/user'
import { userPreferenceModel } from '../models/userPreference'
import { useSignOut } from './useSignOut'

const applicationGetUserInfoModel = z.object({
  id: z.string(),
  applicationKey: z.string(),
  title: z.string(),
  tag: z.array(z.string()),
  requiresEntityAccount: z.boolean(),
  isActive: z.boolean(),
})

export const userInfoDTO = userModel.extend({
  UserPreference: userPreferenceModel,
  Person: personModel,
  ApplicationAccountUsers: applicationAccountUserModel.array(),
  applications: applicationGetUserInfoModel.array().optional(),
  isTermAccepted: z.boolean(),
})

export type ApplicationGetUserInfo = z.infer<typeof applicationGetUserInfoModel>
export type UserInfo = z.infer<typeof userInfoDTO>

export async function getUserInfo() {
  const { data } = await api.backend.get<UserInfo>('/user/get-user-info')

  return data
}

export function useGetUserInfo() {
  const { t } = useTranslation('common')
  const { signOut } = useSignOut()

  const token = Cookies.get(COOKIES_KEYS.token)

  const { data, isLoading, error } = useQuery<UserInfo, AxiosError<ApiError>>({
    queryKey: ['/user/get-user-info', token],
    queryFn: getUserInfo,
    enabled: !!token,
  })

  useEffect(() => {
    if (error) {
      toast(Toast, {
        toastId: 'getUserInfoError',
        data: {
          type: 'danger',
          icon: OctagonAlert,
          title: t('get_user_info.toast.title'),
          description:
            error?.response?.data?.friendlyMessage ||
            t('get_user_info.toast.message'),
        },
      })
      signOut()
    }
  }, [error, t, signOut])

  useEffect(() => {
    if (!data) return

    Sentry.setUser({
      id: String(data.id),
      email: data.email,
      username: data.UserPreference.displayName,
    })
  }, [data])

  return {
    userInfo: data,
    isPendingUserInfo: isLoading,
  }
}
